import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { useTemplate } from './TemplateContext';
import { useContentMode } from '@/hooks/useContentMode';
import type { ProTemplate, ServiceItem, Testimonial, FAQItem, ServiceArea } from '@/config/templates';

export interface MergedSiteContent {
  // Hero
  heroHeadline: string;
  heroSubheadline: string;
  heroCTAPrimary: string;
  heroCTASecondary: string;
  heroBadges: string[];
  heroImageUrl: string | null;
  heroVideoUrl: string | null;
  
  // Services
  services: ServiceItem[];
  
  // Why Choose Us / Differentiators
  differentiators: Array<{ title: string; description: string; icon: string }>;
  
  // Testimonials
  testimonials: Testimonial[];
  
  // FAQ
  faq: FAQItem[];
  
  // Offers
  offers: Array<{ title: string; description: string; discount: string; code?: string; expires?: string }>;
  
  // Process
  processSteps: Array<{ step: number; title: string; description: string }>;
  
  // About
  aboutTitle: string;
  aboutDescription: string;
  aboutHighlights: string[];
  aboutImageUrl: string | null;
  
  // Service Areas
  serviceAreas: ServiceArea[];
  
  // Featured Projects
  featuredProjectsTitle: string;
  featuredProjectsSubtitle: string;

  // SEO
  seoTitle: string;
  seoDescription: string;
  seoCanonical: string;
  
  // Footer
  footerTagline: string;
  
  // Template reference (for things not in DB)
  template: ProTemplate;
  
  // Loading state
  isLoading: boolean;
}

interface SiteContentContextValue extends MergedSiteContent {}

const SiteContentContext = createContext<SiteContentContextValue | undefined>(undefined);

// Helper to merge DB data with template defaults.
// In both CMS and template mode, fall back to template defaults when the
// DB value is empty (null, undefined, blank string, or empty array).
// This ensures the site never shows blank sections just because the CMS
// field hasn't been populated yet.
const mergeWithDefaults = <T,>(dbValue: T | null | undefined, defaultValue: T, _cmsMode: boolean): T => {
  if (dbValue === null || dbValue === undefined) return defaultValue;
  if (Array.isArray(dbValue) && dbValue.length === 0) return defaultValue;
  if (typeof dbValue === 'string' && dbValue.trim() === '') return defaultValue;
  return dbValue;
};

export const SiteContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { template } = useTemplate();
  const { isHydraMode, isCustomMode } = useContentMode();
  // Both Hydra and Custom modes source content from the database (no template fallback)
  const useDbContent = isHydraMode || isCustomMode;

  const { data: dbContent, isLoading } = useQuery({
    queryKey: ['site-content-public'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('site_content')
        .from('site_content')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching site content:', error);
        return null;
      }

      return data;
    },
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
  });

  const mergedContent = useMemo<MergedSiteContent>(() => {
    // Convert template service areas to the format we need
    const templateServiceAreas = template.serviceAreas || [];
    
    // Convert template differentiators from about.whyChooseUs
    const templateDifferentiators = (template.about?.whyChooseUs || []).map((item, i) => ({
      title: item,
      description: '',
      icon: 'CheckCircle',
    }));

    // Template process steps
    const templateProcess = template.process?.map(p => ({
      step: p.step,
      title: p.title,
      description: p.description,
    })) || [];

    // DB service areas - convert from string[] if needed
    const dbServiceAreas: ServiceArea[] = Array.isArray(dbContent?.service_areas) 
      ? (dbContent.service_areas as unknown as Array<string | ServiceArea>).map((area) => 
          typeof area === 'string' ? { name: area, description: '' } : area
        )
      : [];

    return {
      // Hero
      heroHeadline: mergeWithDefaults(dbContent?.hero_headline, template.hero?.headline || '', useDbContent),
      heroSubheadline: mergeWithDefaults(dbContent?.hero_subheadline, template.hero?.subheadline || '', useDbContent),
      heroCTAPrimary: mergeWithDefaults(dbContent?.hero_cta_primary, template.hero?.ctaPrimary || 'Schedule Service', useDbContent),
      heroCTASecondary: mergeWithDefaults(dbContent?.hero_cta_secondary, template.hero?.ctaSecondary || 'Call Now', useDbContent),
      heroBadges: mergeWithDefaults(
        Array.isArray(dbContent?.hero_badges) ? dbContent.hero_badges as unknown as string[] : [],
        template.trustBadges?.map(b => b.label) || [],
        useDbContent
      ),
      heroImageUrl: dbContent?.hero_image_url || null,
      heroVideoUrl: dbContent?.hero_video_url || null,

      // Services
      services: mergeWithDefaults(
        Array.isArray(dbContent?.services) ? dbContent.services as unknown as ServiceItem[] : [],
        template.services || [],
        useDbContent
      ),

      // Differentiators
      differentiators: mergeWithDefaults(
        Array.isArray(dbContent?.differentiators) 
          ? dbContent.differentiators as unknown as Array<{ title: string; description: string; icon: string }>
          : [],
        templateDifferentiators,
        useDbContent
      ),

      // Testimonials
      testimonials: mergeWithDefaults(
        Array.isArray(dbContent?.testimonials) ? dbContent.testimonials as unknown as Testimonial[] : [],
        template.testimonials || [],
        useDbContent
      ),

      // FAQ
      faq: mergeWithDefaults(
        Array.isArray(dbContent?.faq) ? dbContent.faq as unknown as FAQItem[] : [],
        template.faq || [],
        useDbContent
      ),

      // Offers
      offers: Array.isArray(dbContent?.offers) 
        ? dbContent.offers as unknown as Array<{ title: string; description: string; discount: string; code?: string; expires?: string }>
        : [],

      // Process
      processSteps: mergeWithDefaults(
        Array.isArray(dbContent?.process_steps) 
          ? dbContent.process_steps as unknown as Array<{ step: number; title: string; description: string }>
          : [],
        templateProcess,
        useDbContent
      ),

      // About
      aboutTitle: mergeWithDefaults(dbContent?.about_title, 'About Us', useDbContent),
      aboutDescription: mergeWithDefaults(dbContent?.about_description, template.about?.story || '', useDbContent),
      aboutHighlights: mergeWithDefaults(
        Array.isArray(dbContent?.about_highlights) ? dbContent.about_highlights as string[] : [],
        template.about?.values || [],
        useDbContent
      ),
      aboutImageUrl: dbContent?.about_image_url || null,

      // Featured Projects
      featuredProjectsTitle: (dbContent as any)?.featured_projects_title || (useDbContent ? '' : 'Featured Projects'),
      featuredProjectsSubtitle: (dbContent as any)?.featured_projects_subtitle || (useDbContent ? '' : 'See examples of our quality work across various project types.'),

      // Service Areas
      serviceAreas: mergeWithDefaults(dbServiceAreas, templateServiceAreas, useDbContent),

      // SEO
      seoTitle: mergeWithDefaults(dbContent?.seo_title, `${template.brand?.name || 'Home'} | Professional Services`, useDbContent),
      seoDescription: mergeWithDefaults(dbContent?.seo_description, template.hero?.subheadline || '', useDbContent),
      seoCanonical: dbContent?.seo_canonical || '',

      // Footer
      footerTagline: mergeWithDefaults(dbContent?.footer_tagline, template.brand?.tagline || '', useDbContent),

      // Template reference
      template,

      // Loading state
      isLoading,
    };
  }, [dbContent, template, isLoading, useDbContent]);

  return (
    <SiteContentContext.Provider value={mergedContent}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within SiteContentProvider');
  }
  return context;
};
